import { PercentPipe } from '@angular/common';
import { Injectable } from '@angular/core';
import dateFormat from 'dateformat';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import _ from 'lodash-es';
import { MetadataField } from '../enums/metadata-field.enum';
// import { UserCategory } from '../enums/user-category.enum';
import { ClassificationItem } from '../models/classification-item.model';
import { ClassificationReport } from '../models/classification-report.model';
import { ClassificationResult } from '../models/classification-result.model';
import { Metadata } from '../models/metadata.model';
import { RegionBox } from '../models/region-box.model';
import { Size } from '../models/size.model';
// import { StatisticsRecord } from '../models/statistics-record.model';
import { Const } from '../static/const';
import { IconService } from './icon.service';
import { Converters } from '../static/converters';
// import { AuthService } from './auth.service';
// import { I18nService } from './i18n.service';
// import { IconService } from './icon.service';

@Injectable({
  providedIn: 'root',
})
export class ExcelService {
  private static originalMaxSize: Size = { width: 300, height: 300 };
  private static regionMaxSize: Size = { width: 300, height: 150 };

  constructor(
    // private i18nService: I18nService,
    // private percentPipe: PercentPipe,
    // private authService: AuthService,
    private iconService: IconService
  ) {}

  private createWorkbook(): ExcelJS.Workbook {
    const workbook: ExcelJS.Workbook = new ExcelJS.Workbook();
    workbook.creator = 'Test_User';
    workbook.lastModifiedBy = 'Test_user';
    workbook.created = new Date();
    workbook.modified = new Date();
    return workbook;
  }

  private async workbookToBlob(workbook: ExcelJS.Workbook): Promise<Blob> {
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: Const.excelFileType });

    return blob;
  }

  async getReportFile(report: any): Promise<File> {
    try {
      const workbook = this.createWorkbook();
      const sizes = await Promise.all(
        report.images?.map(async (base64) =>
          Converters.getPngDimensions(base64)
        )
      );
      await this.addMetadataSheet(workbook, report, sizes);
      this.addResultsSheet(workbook, report, sizes);
      if (report.disclaimers.length) {
        this.addDisclaimersSheet(workbook, report, sizes);
      }
      const blob = await this.workbookToBlob(workbook);
      return new File([blob], this.getReportName(report));
    } catch (err) {
      console.error(err);
      throw err;
    }
  }

  async exportReport(report: ClassificationReport) {
    console.log('report', report);
    const file = await this.getReportFile(report);
    saveAs(file, file.name);
  }

  // private getUserRoleAndCategory(): string {
  //   if (!this.authService.user) {
  //     return '';
  //   }
  //   const role = this.i18nService.translateInstant(
  //     `ENUMS.USER_ROLE.${this.authService.user.role}`
  //   );
  //   // const category = this.getUserCategory(this.authService.user.category);
  //   const category = 'LAUNDRY'
  //   return `${role}, ${category}`;
  // }

  // private getUserCategory(category: string) {
  //   if (category in UserCategory) {
  //     return `${category}`;
  //   }
  //   return category;
  // }

  private async addMetadataSheet(
    workbook: ExcelJS.Workbook,
    report: any,
    imageSizes: Size[]
  ) {
    const worksheet: any = workbook.addWorksheet('Metadata', {
      pageSetup: {
        orientation: 'portrait',
        fitToPage: true,
        fitToWidth: 1,
        fitToHeight: 1,
      },
      views: [{}],
    });
    const logoId = workbook.addImage({
      base64: await this.iconService.getImage(
        './assets/images/logos/logo-min.png'
      ),
      extension: 'png',
    });
    worksheet.addImage(logoId, {
      tl: { col: 0, row: 0 },
      br: { col: 1.5, row: 5 },
    });

    const metadataFields = _.values(MetadataField);
    const data = [
      ['', '', 'Risk Assessment Result'],
      [
        '',
        '',
        'Created at ' + new Date(report.timestamp).toLocaleString('en-US'),
        ,
      ],
      [],
      // ['', '', this.authService.user.mail || ''],
      // ['', '', this.getUserRoleAndCategory()],
      [],
    ];
    worksheet.addRows(data);

    if (!_.isEmpty(report.metadata)) {
      const tableColumns = metadataFields.map<ExcelJS.TableColumnProperties>(
        (field) => {
          return {
            name: `${field}`,
          };
        }
      );
      const tableRows: any = [];
      metadataFields.forEach((field, colIndex) => {
        const metadataColumn = this.getMetadataColumn(report.metadata, field);
        metadataColumn.forEach((value, rowIndex) => {
          tableRows[rowIndex] = tableRows[rowIndex] || [];
          tableRows[rowIndex][colIndex] = value;
        });
      });
      worksheet.addTable({
        name: 'Metadata',
        ref: `A${worksheet.rowCount}`,
        headerRow: true,
        totalsRow: false,
        columns: tableColumns,
        rows: tableRows,
      });

      worksheet.addRow([]);
      worksheet.getColumn('A').width = 20;
      worksheet.getColumn('A').alignment = { wrapText: true };
      worksheet.getColumn('B').width = 30;
      worksheet.getColumn('B').alignment = { wrapText: true };
      worksheet.getColumn('C').width = 25;
      worksheet.getColumn('C').alignment = { wrapText: true };
      worksheet.getColumn('D').width = 20;
      worksheet.getColumn('D').alignment = { wrapText: true };
    }

    if (report.images?.length) {
      worksheet.addRow(['Original Images']);
      const imageIds = report.images.map((base64) =>
        workbook.addImage({ base64, extension: 'png' })
      );
      const firstRow = worksheet.rowCount;
      imageIds.forEach((imageId, index) => {
        const currentRow = firstRow + index;
        const displaySize = this.getImageDisplaySize(
          imageSizes[index],
          ExcelService.originalMaxSize
        );
        worksheet.addImage(imageId, {
          tl: { col: 0, row: currentRow + 0.05 },
          ext: displaySize,
          editAs: 'oneCell',
        });

        worksheet.getRow(currentRow + 1).height = displaySize.height / 1.3;
      });
      return worksheet;
    }

    if (report.text?.length) {
      let text;
      const reportText = report.text?.includes(' ! ');
      if (reportText) {
        text = report.text.split('!').map((value) => [value]);
      } else {
        text = report.text.split('\n\n\n').map((value) => [value]);
      }

      worksheet.addRow(['Original Text']);
      worksheet.addRows(text);
    }
    return worksheet;
  }

  private getImageDisplaySize(imageSize: Size, maxSize: Size): Size {
    const coeff = Math.max(
      imageSize.width / maxSize.width,
      imageSize.height / maxSize.height
    );
    return { width: imageSize.width / coeff, height: imageSize.height / coeff };
  }

  private getRegionDisplaySize(
    imageSize: Size,
    maxSize: Size,
    region: RegionBox
  ): Size {
    const width = imageSize.width * (region.rightBottomX - region.leftTopX);
    const height = imageSize.height * (region.rightBottomY - region.leftTopY);
    return this.getImageDisplaySize({ width, height }, maxSize);
  }

  private getMetadataColumn(metadata: Metadata, name: string) {
    let metadataValue: any | any[];
    switch (name) {
      case MetadataField.PROJECT: {
        metadataValue = metadata.project;
        break;
      }

      case MetadataField.PRODUCT: {
        metadataValue = metadata.product;
        break;
      }
      case MetadataField.PRODUCT_FORMAT: {
        metadataValue = metadata.productFormat;
        break;
      }
      case MetadataField.GEOGRAPHY: {
        metadataValue = metadata.geography;
        break;
      }
    }

    if (!metadataValue) {
      return [];
    }

    if (metadataValue instanceof Array) {
      return metadataValue.map((value) => `${value}`);
    } else {
      return [`${metadataValue}`];
    }
  }

  private addResultsSheet(
    workbook: ExcelJS.Workbook,
    report: any,
    imageSizes: Size[]
  ): ExcelJS.Worksheet {
    const worksheet = workbook.addWorksheet('Results', {
      pageSetup: {
        orientation: 'landscape',
        fitToPage: true,
        fitToWidth: 1,
        fitToHeight: 1,
        //showGridLines:true
      },
      views: [{ state: 'frozen', xSplit: 0, ySplit: 1 }],
    });

    try {
      const total = report.results[report.total];

      const totalLevelValue = total.level.manualTag || total.level;
      const totalColors = Const.reportTotalColors[+totalLevelValue];
      const tableColumns: ExcelJS.TableColumnProperties[] = [
        {
          name: `Image/Text`,
          filterButton: true,
          totalsRowLabel: 'Overall',
        },
        {
          name: `Page No`,
          filterButton: true,
        },
        {
          name: `Label`,
          filterButton: true,
        },
        {
          name: `Level`,
          filterButton: true,
          totalsRowLabel: this.getValue(total.level),
        },
        {
          name: `Level Rules/Model Score`,
          filterButton: true,
        },
        {
          name: `Manual Level`,
          filterButton: true,
          // totalsRowLabel: this.getValue(total.manual_level.prediction),
        },
        {
          name: `Risk`,
          filterButton: true,
          totalsRowLabel: this.getRiskValue(total.level, total.risk),
        },
        {
          name: `Risk Rules/Model Score`,
          filterButton: true,
        },
        {
          name: `Manual Risk`,
          filterButton: true,
          // totalsRowLabel: this.getValue(total.manual_risk.prediction),
        },
        {
          name: `Reason 1`,
          filterButton: true,
          totalsRowLabel: total.reason[0],
        },
        {
          name: `Reason 1 Rules/Model Score`,
          filterButton: true,
        },
        {
          name: `Manual Reason 1`,
          filterButton: true,
          // totalsRowLabel: total.manual_reason.prediction[0],
        },
        {
          name: `Reason 2`,
          filterButton: true,
          totalsRowLabel: total.reason[1] ? total.reason[1] : '',
        },
        {
          name: `Reason 2 Rules/Model Score`,
          filterButton: true,
        },
        {
          name: `Manual Reason 2`,
          filterButton: true,
          // totalsRowLabel: total.manual_reason.prediction[1]
          //   ? total.manual_reason.prediction[1]
          //   : '',
        },
        {
          name: `Reason 3`,
          filterButton: true,
          totalsRowLabel: total.reason[2] ? total.reason[2] : '',
        },
        {
          name: `Reason 3 Rules/Model Score`,
          filterButton: true,
        },
        {
          name: `Manual Reason 3`,
          filterButton: true,
          // totalsRowLabel: total.manual_reason.prediction[2]
          //   ? total.manual_reason.prediction[2]
          //   : '',
        },
      ];

      const tableRows: any = [];
      report.results.forEach((result, index) => {
        if ((result as any).isDeleted) {
          return;
        }
        const row: any = this.getReportRow(result);
        tableRows.push(row);
        if (result.image !== 'NULL') {
          // const displaySize = this.getRegionDisplaySize(
          //   imageSizes[result.page_no],
          //   ExcelService.regionMaxSize,
          //   result?.image
          // );

          const imageId = workbook.addImage({
            base64: result.image,
            extension: 'png',
          });
          worksheet.getRow(index + 2).height = 200 / 1.3;
          worksheet.addImage(imageId, {
            tl: { col: 0, row: index + 1.05 },
            ext: { width: 200, height: 150 },
            editAs: 'oneCell',
          });
        }
      });
      tableRows.push(this.getTotalRow(report));

      const table = worksheet.addTable({
        name: 'Results',
        ref: `A1`,
        headerRow: true,
        totalsRow: false,
        columns: tableColumns,
        rows: tableRows,
      });

      //Record where the table starts
      const firstTableRowNum = 1;

      //Record table's last row
      let lastRowNum = tableRows.length + 1;
      const lastTableRowNum = lastRowNum;

      //Loop through all table's row
      for (let i = firstTableRowNum; i <= lastTableRowNum; i++) {
        const row = worksheet.getRow(i);
        //Now loop through every row's cell and finally set alignment
        row.eachCell({ includeEmpty: true }, (cell) => {
          cell.alignment = {
            wrapText: true,
          };
        });
      }

      for (let col = 1; col <= 18; col++) {
        const cell = worksheet.getCell(worksheet.rowCount, col);
        cell.border = {
          top: { color: { argb: totalColors?.textArgb } },
          left: { color: { argb: totalColors?.textArgb } },
          bottom: { color: { argb: totalColors?.textArgb } },
          right: { color: { argb: totalColors?.textArgb } },
        };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: totalColors?.backgroundArgb },
        };
        cell.font = {
          color: { argb: totalColors?.textArgb },
        };
      }
    } catch (err) {
      console.error(err);
    }

    worksheet.getColumn('A').width = 40;
    worksheet.getColumn('E').width = 25;
    worksheet.getColumn('H').width = 25;
    worksheet.getColumn('J').width = 40;
    worksheet.getColumn('K').width = 25;
    worksheet.getColumn('L').width = 40;
    worksheet.getColumn('M').width = 40;
    worksheet.getColumn('N').width = 25;
    worksheet.getColumn('O').width = 40;
    worksheet.getColumn('P').width = 40;
    worksheet.getColumn('Q').width = 25;
    worksheet.getColumn('R').width = 40;
    return worksheet;
  }

  private getReportName(classificationReport: ClassificationReport) {
    const formattedDate = dateFormat(
      new Date(classificationReport.timestamp),
      Const.filenameDateFormat
    );
    return `Risk Assessment Report ${formattedDate}.xlsx`;
  }

  private getReportRow(result: any): any[] {
    const row = [
      result.text !== 'NULL' ? result.text : '',
      this.getPageNo(result.page_no),
      result.image != 'NULL' ? this.convertToTitleCase(result.Label) : 'Text',
      +this.getValue(result.level),
      this.getLevelMethodOrConfidence(result.level_model),
      this.getValue(result.manual_level),
      this.getRiskValue(result.level, result.risk),
      this.getRiskMethodOrConfidence(result.level, result.risk_model),
      this.getValue(result.manual_risk),
    ];

    const reasonValues: any = [];
    const reasonData: any = [];

    for (let i = 0; i < result.reason.length; i++) {
      if (result.reason_keywords.length == 0) {
        // to push model value and reason value to single array
        reasonValues.push({
          reason: result.reason[i],
          confidence: result.reason_model[i],
        });
      }
    }

    //to remove reason having '0' value
    reasonValues.forEach((v) => {
      if (v.reason != '0') {
        reasonData.push(v);
      }
    });

    reasonData.forEach((reason, index) => {
      if (reason.manualTag === Const.deletedReason) {
        return;
      }
      row.push(this.getReasonName(reason.reason));
      row.push(this.getMethodOrConfidence(reason.confidence));
      result.manual_reason.map((response) => {
        row.push(this.getReasonName(response));
      });
    });

    return row;
  }

  private getTotalRow(report: any): any[] {
    const total = report.results[report.total];
    const row = [
      'Overall',
      '',
      '',
      +this.getValue(total.level),
      '',
      '',
      +this.getRiskValue(total.level, total.risk),
      '',
      '',
    ];
    total.reason.forEach((reason, index) => {
      if (reason.manualTag === Const.deletedReason) {
        return;
      }
      row.push(this.getReasonName(reason), '', '');
    });
    return row;
  }

  private getPageNo(index) {
    const fileType = localStorage.getItem('fileType');
    if (fileType != null && fileType == 'application/pdf') {
      return index + 1;
    } else {
      return '-';
    }
  }

  private convertToTitleCase(label) {
    return label
      .toLowerCase()
      .split(' ')
      .map((s) => {
        let letterToCapitalize = s.match(/\w/)[0];
        return s.replace(letterToCapitalize, letterToCapitalize.toUpperCase());
      })
      .join(' ');
  }

  private getValue(classificationItem: ClassificationItem): any {
    return classificationItem?.manualTag || classificationItem;
  }

  private getRiskValue(level, risk): any {
    if (level == 2 || level == '2') {
      return +risk?.manualTag || +risk;
    } else {
      return ' ';
    }
  }

  private getMethodOrConfidence(item: any): string {
    if (item?.manualTag) {
      if (item.manualTag === item) {
        return 'approved';
      }
      return 'manual';
    }
    if (item?.rule) {
      return 'By rules';
    }
    return item != 'By Rules' ? this.getConfidence(item) : item;
  }

  private getConfidence(classificationItem: any): any {
    if (classificationItem == null || classificationItem == undefined) {
      return '';
    }
    if (classificationItem < 0.1) {
      return '< 10%';
    } else {
      // return this.percentPipe.transform(classificationItem, '1.0-0');
      return (classificationItem * 100).toFixed(2) + '%';
    }
  }

  private getLevelMethodOrConfidence(item: any): string {
    if (item?.manualTag) {
      if (item.manualTag === item) {
        return 'approved';
      }
      return 'manual';
    }
    if (item?.rule) {
      return 'By Rules';
    }
    return item != 'By Rules' ? this.getLevelConfidence(item) : item;
  }

  private getLevelConfidence(classificationItem: any): any {
    if (
      classificationItem == null ||
      classificationItem == undefined ||
      classificationItem == ' '
    ) {
      return '';
    }
    if (classificationItem < 0.1) {
      return '< 10%';
    } else {
      // return this.percentPipe.transform(classificationItem, '1.0-0');
      return (classificationItem * 100).toFixed(2) + '%';
    }
  }

  private getRiskMethodOrConfidence(level, item: any): string {
    if (item?.manualTag) {
      if (item.manualTag === item) {
        return 'approved';
      }
      return 'manual';
    }
    if (item?.rule) {
      return 'By rules';
    }
    return item != 'By Rules' ? this.getRiskConfidence(level, item) : item;
  }

  private getRiskConfidence(level, classificationItem: any): any {
    if (level == 2 || level == '2') {
      if (classificationItem == null || classificationItem == undefined) {
        return '';
      }
      if (classificationItem < 0.1) {
        return '< 10%';
      } else {
        // return this.percentPipe.transform(classificationItem, '1.0-0');
        return (classificationItem * 100).toFixed(2) + '%';
      }
    }
  }
  private getReasonName(classificationItem: any): string {
    return classificationItem ? classificationItem : '';
  }

  private addDisclaimersSheet(
    workbook: ExcelJS.Workbook,
    report: ClassificationReport,
    imageSizes: Size[]
  ): ExcelJS.Worksheet {
    const worksheet = workbook.addWorksheet('Qualifiers', {
      pageSetup: {
        orientation: 'landscape',
        fitToPage: true,
        fitToWidth: 1,
        fitToHeight: 1,
      },
      views: [{ state: 'frozen', xSplit: 0, ySplit: 1 }],
    });
    try {
      const tableColumns: ExcelJS.TableColumnProperties[] = [
        { name: 'Qualifiers' },
      ];

      const tableRows: any = [];
      report.qualifiers.forEach((disclaimer, index) => {
        tableRows.push([disclaimer]);
      });

      const table = worksheet.addTable({
        name: 'Qualifiers',
        ref: `A1`,
        headerRow: true,
        totalsRow: false,
        columns: tableColumns,
        rows: tableRows,
      });
    } catch (err) {
      console.error(err);
    }

    worksheet.getColumn('A').width = 42.29;
    return worksheet;
  }
}
